<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarRoot,
  DatePickerAnchor,
  DatePickerContent,
  DatePickerRoot,
  useDateFormatter,
} from "reka-ui";
import {
  CalendarDate,
  CalendarDateTime,
  type DateValue,
  getLocalTimeZone,
  parseDate,
  parseDateTime,
  today,
} from "@internationalized/date";
import BButton from "@/components/common/bButton.vue";
import MaterialSymbolsCalendarMonthOutlineRounded from "~icons/material-symbols/calendar-month-outline-rounded";
import MaterialSymbolsLightChevronLeftRounded from "~icons/material-symbols-light/chevron-left-rounded";
import MaterialSymbolsLightChevronRightRounded from "~icons/material-symbols-light/chevron-right-rounded";

import { bgColors, size as sizeConst } from "@/services/application/constantes";
import SolarCloseSquareBroken from "~icons/solar/close-square-broken";
import { displayLocale } from '@/services/application/displayLocale';

type UnavailablePreset = "weekends" | "sundays" | "saturdays";
type UnavailableMatcher = (date: DateValue) => boolean;

const props = withDefaults(
  defineProps<{
    bg?: keyof typeof bgColors;
    size?: keyof typeof sizeConst;
    triggerClass?: string;
    label?: string;
    mode?: "datetime" | "date" | "month";
    subtitle?: string;
    allowClear?: boolean;
    errorMessage?: string;
    is24hr?: boolean;
    minDate?: Date;
    shouldGrow?: boolean;
    required?: boolean;
    isDateUnavailable?: UnavailablePreset | UnavailableMatcher;
    /** View shown when the picker opens on an empty value, e.g. `years` for a birthdate. */
    startView?: "days" | "months" | "years";
    /** With `startView`: how many years back from today an empty value opens. */
    startYearsAgo?: number;
  }>(),
  {
    mode: "date",
    triggerClass: "",
    bg: "base",
    size: "small",
    allowClear: false,
    shouldGrow: false,
    required: false,
  },
);

const pickerValue = defineModel<string | Date | null | undefined>();

const { t } = useI18n();
const lang = displayLocale;

/** Floating field label above the trigger (aligns with TextInput). */
const floatingLabel = computed(() => props.subtitle || "");
/** Placeholder inside the trigger when no date is selected. */
const emptyLabel = computed(() => {
  if (props.subtitle) return props.label || "";
  return props.label || "";
});

const formatter = useDateFormatter(lang.value);

// Convert minDate to CalendarDate for reka-ui
// Using 'any' to avoid type mismatch between @internationalized/date versions
const minDateValue = computed<any>(() => {
  if (!props.minDate) return undefined;
  const d = props.minDate;
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
});

// Resolve isDateUnavailable prop to a matcher function
// Using 'any' to avoid type mismatch between @internationalized/date versions
const unavailableMatcher = computed<((date: any) => boolean) | undefined>(() => {
  if (!props.isDateUnavailable) return undefined;
  if (typeof props.isDateUnavailable === "function") return props.isDateUnavailable;

  switch (props.isDateUnavailable) {
    case "weekends":
      return (date: DateValue) => {
        const dow = date.toDate(getLocalTimeZone()).getDay();
        return dow === 0 || dow === 6;
      };
    case "sundays":
      return (date: DateValue) => date.toDate(getLocalTimeZone()).getDay() === 0;
    case "saturdays":
      return (date: DateValue) => date.toDate(getLocalTimeZone()).getDay() === 6;
    default:
      return undefined;
  }
});

// Internal date value for reka-ui (CalendarDate or CalendarDateTime)
// Uses computed get/set for two-way binding with external Date/string value
// Using 'any' to avoid type mismatch between @internationalized/date versions
const internalValue = computed<any>({
  get() {
    const val = pickerValue.value;
    if (!val) return undefined;

    if (val instanceof Date) {
      if (props.mode === "datetime") {
        return new CalendarDateTime(
          val.getFullYear(),
          val.getMonth() + 1,
          val.getDate(),
          val.getHours(),
          val.getMinutes(),
          val.getSeconds(),
        );
      }
      return new CalendarDate(val.getFullYear(), val.getMonth() + 1, val.getDate());
    }

    if (typeof val === "string") {
      try {
        if (props.mode === "datetime" && val.includes("T")) {
          return parseDateTime(val.substring(0, 19));
        }
        return parseDate(val.substring(0, 10));
      } catch {
        const d = new Date(val);
        if (!Number.isNaN(d.getTime())) {
          if (props.mode === "datetime") {
            return new CalendarDateTime(
              d.getFullYear(),
              d.getMonth() + 1,
              d.getDate(),
              d.getHours(),
              d.getMinutes(),
            );
          }
          return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
        }
      }
    }
    return undefined;
  },
  set(val) {
    if (!val && !props.allowClear) return;
    pickerValue.value = val ? val.toDate(getLocalTimeZone()) : null;
  },
});

// Formatted display value for the button
const displayValue = computed(() => {
  if (!internalValue.value) return "";

  const date = internalValue.value.toDate(getLocalTimeZone());
  if (props.mode === "datetime") {
    return formatter.custom(date, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: !props.is24hr,
    });
  }
  if (props.mode === "month") {
    return formatter.custom(date, {
      month: "long",
      year: "numeric",
    });
  }
  return formatter.custom(date, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
});

const placeholder = ref(internalValue.value || today(getLocalTimeZone()));

type CalendarView = "days" | "months" | "years";
const view = ref<CalendarView>(props.mode === "month" ? "months" : "days");
const isOpen = ref(false);

const yearRange = computed(() => {
  const year = placeholder.value.year;
  const startYear = Math.floor(year / 12) * 12;
  return Array.from({ length: 12 }, (_, i) => startYear + i);
});

function goToMonthView() {
  view.value = "months";
}

function goToYearView() {
  view.value = "years";
}

function isMonthBeforeMin(month: number) {
  if (!props.minDate) return false;
  const year = placeholder.value.year;
  const minYear = props.minDate.getFullYear();
  const minMonth = props.minDate.getMonth() + 1;
  return year < minYear || (year === minYear && month < minMonth);
}

function selectMonth(month: number) {
  placeholder.value = placeholder.value.set({ month, day: 1 });
  if (props.mode === "month") {
    internalValue.value = placeholder.value;
    isOpen.value = false;
    return;
  }
  view.value = "days";
}

function selectYear(year: number) {
  placeholder.value = placeholder.value.set({ year });
  view.value = "months";
}

function prevRange() {
  if (view.value === "years") {
    placeholder.value = placeholder.value.subtract({ years: 12 });
  } else if (view.value === "months") {
    placeholder.value = placeholder.value.subtract({ years: 1 });
  } else {
    placeholder.value = placeholder.value.subtract({ months: 1 });
  }
}

function nextRange() {
  if (view.value === "years") {
    placeholder.value = placeholder.value.add({ years: 12 });
  } else if (view.value === "months") {
    placeholder.value = placeholder.value.add({ years: 1 });
  } else {
    placeholder.value = placeholder.value.add({ months: 1 });
  }
}

// Time handling for datetime mode
const hours = ref(0);
const minutes = ref(0);

watch(
  internalValue,
  (val) => {
    if (val && "hour" in val) {
      hours.value = val.hour;
      minutes.value = val.minute;
    }
  },
  { immediate: true },
);

function updateTime() {
  if (internalValue.value) {
    internalValue.value = (internalValue.value as CalendarDateTime).set({
      hour: hours.value,
      minute: minutes.value,
    });
  }
}

// Clear function
function clearValue() {
  internalValue.value = null;
}

const togglePopover = () => {
  isOpen.value = !isOpen.value;
};

watch(isOpen, (open) => {
  // Open on the selected date: the value may have changed since the picker was created (a shortcut)
  if (open && internalValue.value) placeholder.value = internalValue.value;
  if (open && props.mode === "month") view.value = "months";
  // A date far from today (a birthdate) is quicker to reach from the year grid.
  if (open && !internalValue.value && props.startView) {
    view.value = props.startView;
    if (props.startYearsAgo)
      placeholder.value = today(getLocalTimeZone()).subtract({ years: props.startYearsAgo });
  }
});

defineExpose({
  togglePopover,
});
</script>

<template>
  <div class="flex gap-1 items-end" :class="shouldGrow ? 'min-w-0 grow w-full' : 'shrink-0'">
    <DatePickerRoot
      v-model="internalValue"
      close-on-select
      v-model:open="isOpen"
      :locale="lang"
      :min-value="minDateValue"
      :is-date-unavailable="unavailableMatcher"
      :granularity="mode === 'datetime' ? 'minute' : 'day'"
      class="relative min-w-0 w-full"
      :class="{ 'flex-1': shouldGrow }"
    >
      <DatePickerAnchor class="block w-full">
        <slot :input-value="displayValue" :toggle-popover="togglePopover">
          <BButton
            :bg="bg"
            :size="size"
            :class="[triggerClass, { 'mt-5': !!floatingLabel }]"
            class="relative px-2 w-full justify-start"
            @click.prevent="togglePopover"
          >
            <MaterialSymbolsCalendarMonthOutlineRounded class="shrink-0" />
            <span class="truncate">{{ displayValue || emptyLabel }}</span>
            <p
              v-if="floatingLabel"
              class="absolute -top-3 left-0 subtitle -translate-y-1/2 pointer-events-none"
            >
              {{ floatingLabel }} <span v-if="required" class="text-danger-400">*</span>
            </p>
          </BButton>
          <transition name="shake">
            <p v-show="errorMessage" class="text-red-400 text-sm">
              {{ errorMessage }}
            </p>
          </transition>
        </slot>
      </DatePickerAnchor>

      <DatePickerContent
        align="start"
        :side-offset="8"
        class="card-base p-4 z-9999 min-w-[280px]"
      >
        <CalendarRoot
          v-slot="{ weekDays, grid }"
          v-model="internalValue"
          v-model:placeholder="placeholder"
          :locale="lang"
          :min-value="minDateValue"
          :is-date-unavailable="unavailableMatcher"
          class="flex flex-col space-y-4"
          fixed-weeks
        >
          <!-- Keyed by index, not by month: keying on the value remounts the whole block on
               every navigation, so the arrow loses focus and the popover closes on first click. -->
          <div v-for="(month, monthIndex) in grid" :key="monthIndex">
            <!-- Header with navigation -->
            <div class="flex items-center justify-between mb-4">
              <button
                type="button"
                class="inline-flex items-center cursor-pointer justify-center rounded-md bg-transparent w-7 h-7 hover:bg-primary-500/10 transition"
                data-cy="calendar-prev-range"
                @click.stop="prevRange"
              >
                <MaterialSymbolsLightChevronLeftRounded />
              </button>

              <div class="font-semibold flex-1 text-center text-sm">
                <button
                  v-if="view === 'days'"
                  type="button"
                  class="hover:underline"
                  @click="goToMonthView"
                >
                  {{
                    formatter.custom(placeholder.toDate(getLocalTimeZone()), {
                      month: "long",
                      year: "numeric",
                    })
                  }}
                </button>
                <button
                  v-else-if="view === 'months'"
                  type="button"
                  class="hover:underline"
                  @click="goToYearView"
                >
                  {{
                    formatter.custom(placeholder.toDate(getLocalTimeZone()), { year: "numeric" })
                  }}
                </button>
                <span v-else> {{ yearRange[0] }} - {{ yearRange[11] }} </span>
              </div>

              <button
                type="button"
                class="inline-flex items-center cursor-pointer justify-center rounded-md bg-transparent w-7 h-7 hover:bg-primary-500/10 transition"
                data-cy="calendar-next-range"
                @click.stop="nextRange"
              >
                <MaterialSymbolsLightChevronRightRounded />
              </button>
            </div>

            <!-- Days view -->
            <CalendarGrid
              v-if="view === 'days' && mode !== 'month'"
              class="w-full border-collapse select-none space-y-1"
            >
              <CalendarGridHead>
                <CalendarGridRow class="mb-1 flex w-full justify-between">
                  <CalendarHeadCell
                    v-for="day in weekDays"
                    :key="day"
                    class="w-8 rounded-md text-xs text-gray-500 text-center"
                  >
                    {{ day.slice(0, 2) }}
                  </CalendarHeadCell>
                </CalendarGridRow>
              </CalendarGridHead>
              <CalendarGridBody>
                <CalendarGridRow
                  v-for="(weekDates, index) in month.rows"
                  :key="`weekDate-${index}`"
                  class="flex w-full justify-between"
                >
                  <CalendarCell
                    v-for="weekDate in weekDates"
                    :key="weekDate.toString()"
                    :date="weekDate"
                    :data-cy="`id-${weekDate.year}-${String(weekDate.month).padStart(2, '0')}-${String(weekDate.day).padStart(2, '0')}`"
                    class="relative p-0 text-center text-sm has-data-selected:bg-primary-500/20 data-disabled:opacity-30 data-disabled:pointer-events-none"
                  >
                    <CalendarCellTrigger
                      :day="weekDate"
                      :month="month.value"
                      class="relative flex items-center rounded-md justify-center whitespace-nowrap text-sm font-normal w-8 h-8 outline-hidden transition duration-100 hover:bg-primary-500 hover:text-white data-selected:bg-primary-500 data-selected:text-white data-unavailable:pointer-events-none data-unavailable:text-red-500/30 data-unavailable:line-through data-disabled:pointer-events-none data-disabled:opacity-30 before:absolute before:bottom-[3px] before:hidden before:rounded-full before:w-1 before:h-1 data-today:before:block data-today:before:bg-primary-600 data-selected:before:bg-white data-outside-view:text-gray-400 data-outside-view:opacity-50 focus-visible:ring-2 focus-visible:ring-primary-500/70 focus-visible:ring-offset-2"
                    />
                  </CalendarCell>
                </CalendarGridRow>
              </CalendarGridBody>
            </CalendarGrid>

            <!-- Months view -->
            <div v-else-if="view === 'months'" class="grid grid-cols-3 gap-2 w-full">
              <button
                v-for="m in 12"
                :key="m"
                type="button"
                :data-cy="`calendar-month-${m}`"
                class="p-2 rounded-md text-center text-sm hover:bg-primary-500/10 transition"
                :class="{
                  'bg-primary-500 text-white hover:bg-primary-600': placeholder.month === m,
                  'opacity-30 pointer-events-none': isMonthBeforeMin(m),
                }"
                :disabled="isMonthBeforeMin(m)"
                @click="selectMonth(m)"
              >
                {{
                  formatter.custom(placeholder.set({ month: m }).toDate(getLocalTimeZone()), {
                    month: "short",
                  })
                }}
              </button>
            </div>

            <!-- Years view -->
            <div v-else-if="view === 'years'" class="grid grid-cols-4 gap-2 w-full">
              <button
                v-for="year in yearRange"
                :key="year"
                type="button"
                :data-cy="`calendar-year-${year}`"
                class="p-2 rounded-md text-center text-sm hover:bg-primary-500/10 transition"
                :class="{
 'bg-primary-500 text-white hover:bg-primary-600': placeholder.year === year,
                }"
                @click="selectYear(year)"
              >
                {{ year }}
              </button>
            </div>
          </div>

          <!-- Time picker for datetime mode -->
          <div v-if="mode === 'datetime'" class="flex items-center gap-2 pt-2 border-t border-base">
            <span class="text-sm text-gray-500">{{ t("common.time") }}:</span>
            <input
              v-model.number="hours"
              type="number"
              min="0"
              max="23"
              class="w-12 text-center rounded-sm border border-base bg-base px-1 py-0.5 text-sm"
              @change="updateTime"
            />
            <span>:</span>
            <input
              v-model.number="minutes"
              type="number"
              min="0"
              max="59"
              class="w-12 text-center rounded-sm border border-base bg-base px-1 py-0.5 text-sm"
              @change="updateTime"
            />
          </div>
        </CalendarRoot>
      </DatePickerContent>
    </DatePickerRoot>

    <button
      v-if="allowClear && pickerValue"
      type="button"
      class="shrink-0 self-center rounded-full p-2 text-danger-500 transition hover:bg-fade"
      :title="t('common.delete')"
      @click.stop="clearValue"
    >
      <SolarCloseSquareBroken class="h-4 w-4" />
    </button>
  </div>
</template>

<style>
/* Ensure date picker content appears above modals / teleported overlays */
[data-reka-date-picker-content] {
  z-index: 9999 !important;
}
</style>
